import clsx from 'clsx'
import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { CommunityCardSlider } from '@/src/features/community/ui/components/CommunityCardSlider'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { COMMUNITIES_CATEGORY, EVENT_ORIGIN } from '../../../models/community.constants'
import { getCommunitiesCategoryQueryParams, useCommunitiesQuery } from '../../../services/useCommunitiesQuery'
import { useSuggestionsVisibility } from '../../../services/useSuggestionsVisibility'

export const CommunitiesSuggestionsSection = (): ReactNode => {
  const { navigate } = useRouter()
  const { routes } = useRoutes()
  const { lessThan, width } = useDimensions()
  const { t } = useTranslate()
  const visibilityProps = useSuggestionsVisibility()
  const { data: me } = useMeQuery()

  const isWizardEmpty = me && (!me.wizard || !me.wizard.tags || me.wizard.tags.length === 0)

  const { data: suggestedCommunities } = useCommunitiesQuery(
    isWizardEmpty ? { enabled: false } : getCommunitiesCategoryQueryParams(COMMUNITIES_CATEGORY.SUGGESTED),
  )

  const editPreferences = () => navigate(routes.preferences())

  return (
    <>
      <CommunityCardSlider
        testID={TEST_ID.COMMUNITIES.SUGGESTED_COMMUNITIES}
        title={t('community.communities.suggested')}
        classes={{ slider: clsx(width > 1400 && 'mx-6') }}
        containerProps={visibilityProps}
        list={isWizardEmpty ? [] : suggestedCommunities}
        origin={EVENT_ORIGIN.SUGGESTED_SECTION}
        source={TRACKING_SOURCE.SUGGESTED_SECTION}
        link={routes.communitiesCategory(COMMUNITIES_CATEGORY.SUGGESTED)}
        emptyState={
          isWizardEmpty && (
            <View className="mx-auto flex max-w-[740px] gap-6 px-6">
              <Typography center={!lessThan.xm} size={lessThan.xm ? 'md' : 'lg'}>
                {t('communities.for-you.empty')}
              </Typography>
              <Button
                size="lg"
                fullWidth={lessThan.xm}
                variant="solid"
                colors="primary"
                endIcon={ICON.CHEVRON_RIGHT}
                onPress={editPreferences}
              >
                {t('communities.for-you.settings')}
              </Button>
            </View>
          )
        }
      />
      {suggestedCommunities && suggestedCommunities?.length > 0 && (
        <View className="px-6">
          <Button
            size="xs"
            variant="faded"
            colors="primary"
            className="self-start"
            endIcon={ICON.CHEVRON_RIGHT}
            onPress={editPreferences}
          >
            {t('communities.for-you.edit')}
          </Button>
        </View>
      )}
    </>
  )
}
